import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import {
  fetchCreditPaymentsSorted,
  paymentTimestamp,
  fetchAllCreditBills,
  daysOutstandingForBill,
  creditPaymentBranch,
} from "../../services/creditDueService";
import { useAuth } from "../../context/AuthContext";
import { useCreditBranchFilter } from "./useCreditBranchFilter";
import CreditBranchSelector from "./CreditBranchSelector";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function formatAllocationMethod(m) {
  if (m === "fifo") return "FIFO";
  if (m === "manual") return "Manual";
  if (m === "mixed") return "Mixed";
  return m || "—";
}

/** cash vs card/UPI (stored as payMode on creditPayments) */
function formatPayMode(p) {
  if (p?.payMode === "card_upi") return "Card / UPI";
  return "Cash";
}

export default function CreditPaymentHistoryPage() {
  const { user: storedUser } = useAuth();
  const { branch, setBranch, visibleBranches } = useCreditBranchFilter(storedUser);

  const [searchParams] = useSearchParams();
  const filterCustomerId = searchParams.get("customerId") || "";

  const [payments, setPayments] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [customerNames, setCustomerNames] = useState({});
  const [billVouchers, setBillVouchers] = useState({});
  /** bill id → credit days (days from invoice to today, for reference on each line) */
  const [billCreditDays, setBillCreditDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [payRows, billRows, custSnap] = await Promise.all([
        fetchCreditPaymentsSorted(),
        fetchAllCreditBills(),
        getDocs(query(collection(db, "customers"), where("group", "==", "Sundry Debtors"))),
      ]);
      setPayments(payRows);
      setAllBills(billRows);
      const bv = {};
      const bd = {};
      for (const b of billRows) {
        bv[b.id] = b.voucherNo || b.id?.slice(0, 8) || "—";
        bd[b.id] = daysOutstandingForBill(b);
      }
      setBillVouchers(bv);
      setBillCreditDays(bd);
      const nm = {};
      custSnap.docs.forEach((d) => {
        const x = d.data();
        nm[d.id] = x.partyName || x.name || d.id;
      });
      setCustomerNames(nm);
    } catch (e) {
      setErr(e?.message || "Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const billById = useMemo(() => {
    const m = new Map();
    for (const b of allBills) {
      if (b?.id) m.set(b.id, b);
    }
    return m;
  }, [allBills]);

  const paymentsInBranch = useMemo(
    () => payments.filter((p) => creditPaymentBranch(p, billById) === branch),
    [payments, billById, branch]
  );

  const filtered = useMemo(() => {
    let list = paymentsInBranch;
    if (filterCustomerId) {
      list = list.filter((p) => p.customerId === filterCustomerId);
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const name = (customerNames[p.customerId] || "").toLowerCase();
      const note = (p.note || "").toLowerCase();
      const id = (p.customerId || "").toLowerCase();
      return name.includes(q) || note.includes(q) || id.includes(q);
    });
  }, [paymentsInBranch, search, customerNames, filterCustomerId]);

  const totalShown = useMemo(
    () => round2(filtered.reduce((s, p) => s + round2(p.amount), 0)),
    [filtered]
  );

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-5xl px-3 pb-24 pt-4 sm:px-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link to="/credit-due" className="text-sm font-semibold text-indigo-600 hover:underline">
              ← Credit / Due
            </Link>
            <h1 className="mt-2 text-lg font-bold text-slate-900">Payment history</h1>
            <p className="text-xs text-slate-600">Receipts recorded against credit bills (Udhar).</p>
            {filterCustomerId ? (
              <p className="mt-1 text-xs text-indigo-800">
                Showing this customer only —{" "}
                <Link className="font-semibold underline" to={`/credit-due/customer/${filterCustomerId}`}>
                  Profile
                </Link>
                {" · "}
                <Link className="font-semibold underline" to="/credit-due/payments">
                  All customers
                </Link>
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <CreditBranchSelector value={branch} onChange={setBranch} options={visibleBranches} />
            <button
              type="button"
              onClick={load}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="mb-4">
          <input
            type="search"
            placeholder="Search customer, note, customer id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
        </div>

        {err && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>
        )}

        {loading ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-600">No payments recorded yet.</p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700">
              <span>
                Showing <strong>{filtered.length}</strong> receipt{filtered.length !== 1 ? "s" : ""}
              </span>
              <span className="font-semibold tabular-nums">
                Total ₹{totalShown.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="overflow-x-auto overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-bold uppercase text-slate-600">
                  <tr>
                    <th className="p-3 whitespace-nowrap">Date</th>
                    <th className="p-3">Customer</th>
                    <th className="p-3 text-right">Amount</th>
                    <th className="p-3">Paid by</th>
                    <th className="p-3" title="FIFO vs manual allocation">
                      Apply
                    </th>
                    <th className="p-3">Note</th>
                    <th className="p-3 min-w-[12rem]" title="Bill · amount · credit days (from invoice to today)">
                      Allocations
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const ts = paymentTimestamp(p);
                    const allocs = (p.allocations || []).filter((a) => round2(a.amount) > 0);
                    return (
                      <tr key={p.id} className="border-t border-slate-100">
                        <td className="p-3 align-top text-xs text-slate-700 whitespace-nowrap">
                          {ts.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </td>
                        <td className="p-3 align-top text-slate-900">
                          <Link
                            to={`/credit-due/customer/${p.customerId}`}
                            className="font-semibold text-indigo-700 hover:underline"
                          >
                            {customerNames[p.customerId] || "—"}
                          </Link>
                          <div className="font-mono text-[10px] text-slate-500">{p.customerId}</div>
                        </td>
                        <td className="p-3 align-top text-right font-bold tabular-nums text-slate-900">
                          ₹{round2(p.amount).toLocaleString("en-IN")}
                        </td>
                        <td className="p-3 align-top text-xs font-medium text-slate-800">{formatPayMode(p)}</td>
                        <td className="p-3 align-top text-xs text-slate-600">{formatAllocationMethod(p.method)}</td>
                        <td className="p-3 align-top text-xs text-slate-600 max-w-[12rem] break-words">{p.note || "—"}</td>
                        <td className="p-3 align-top text-xs text-slate-700">
                          <ul className="max-h-28 space-y-0.5 overflow-y-auto font-mono text-[11px]">
                            {allocs.map((a) => (
                              <li key={`${p.id}-${a.billId}`}>
                                {billVouchers[a.billId] || a.billId?.slice(0, 8)} · ₹
                                {round2(a.amount).toLocaleString("en-IN")}
                                {billCreditDays[a.billId] != null ? (
                                  <span className="text-slate-500"> · {billCreditDays[a.billId]} d</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
